// Replace faker dependency with simple utility functions

/**
 * Generate mock tickets for customer service dashboard
 * @param {number} count - Number of tickets to generate
 * @returns {Array} - Array of ticket objects
 */
export const generateMockTickets = (count = 10) => {
  const statuses = ['open', 'pending', 'closed', 'urgent'];
  const categories = ['account', 'billing', 'technical', 'feature', 'general'];
  const priorities = ['low', 'medium', 'high'];
  
  return Array.from({ length: count }).map((_, index) => {
    const customer = generateMockUser();
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const priority = priorities[Math.floor(Math.random() * priorities.length)];
    const createdAt = getRandomPastDate(0.1).toISOString();
    const lastActivity = getRandomDateBetween(new Date(createdAt), new Date()).toISOString();
    const id = `TKT-${generateRandomNumericString(4)}`;
    
    // Generate messages
    const messages = [];
    const messageCount = Math.floor(Math.random() * 5) + 1;
    
    // Add initial message from customer
    messages.push({
      id: generateRandomID(),
      senderId: customer.id,
      senderName: customer.name,
      content: getRandomSentence(3, 10),
      timestamp: createdAt,
      read: true
    });
    
    // Add subsequent messages
    for (let i = 1; i < messageCount; i++) {
      const isAgent = i % 2 === 1;
      const timestamp = getRandomDateBetween(new Date(createdAt), new Date(lastActivity)).toISOString();
      
      messages.push({
        id: generateRandomID(),
        senderId: isAgent ? 'agent-' + Math.floor(Math.random() * 5 + 1) : customer.id,
        senderName: isAgent ? 'Support Agent ' + Math.floor(Math.random() * 5 + 1) : customer.name,
        content: getRandomParagraph(1, 3),
        timestamp,
        read: true
      });
    }
    
    // Sort messages by timestamp
    messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Generate subject based on category
    let subject = '';
    switch(category) {
      case 'account':
        subject = pickRandomFromArray([
          'Can\'t access my account',
          'Account verification issue',
          'Need help updating profile',
          'Password reset problem'
        ]);
        break;
      case 'billing':
        subject = pickRandomFromArray([
          'Question about my recent invoice',
          'Payment method update',
          'Refund request',
          'Subscription cancellation'
        ]);
        break;
      case 'technical':
        subject = pickRandomFromArray([
          'Website not loading properly',
          'Error when submitting form',
          'API integration issue',
          'Mobile app crashing'
        ]);
        break;
      case 'feature':
        subject = pickRandomFromArray([
          'Suggestion for new feature',
          'Interface improvement idea',
          'Request for additional functionality',
          'Enhancement proposal'
        ]);
        break;
      default:
        subject = pickRandomFromArray([
          'General inquiry about services',
          'Information request',
          'Need assistance with options',
          'Question about company policies'
        ]);
    }
    
    return {
      id,
      subject,
      description: messages[0]?.content || getRandomSentence(1, 1),
      status,
      priority,
      category,
      customer,
      createdAt,
      lastActivity,
      assignedTo: Math.random() > 0.5 ? `user-${Math.floor(Math.random() * 5) + 1}` : null,
      messages,
      unread: Math.random() > 0.7,
      starred: Math.random() > 0.8
    };
  });
};

/**
 * Generate a mock user
 * @returns {Object} - User object
 */
export const generateMockUser = () => {
  const firstName = getRandomFirstName();
  const lastName = getRandomLastName();
  
  return {
    id: generateRandomID(),
    name: `${firstName} ${lastName}`,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    avatar: getRandomAvatarUrl(),
    phone: getRandomPhoneNumber(),
    company: getRandomCompanyName()
  };
};

/**
 * Generate mock customer service team users
 * @param {number} count - Number of users to generate
 * @returns {Array} - Array of user objects
 */
export const generateMockUsers = (count = 5) => {
  return Array.from({ length: count }).map((_, index) => {
    const firstName = getRandomFirstName();
    const lastName = getRandomLastName();
    
    return {
      id: `user-${index + 1}`,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      avatar: getRandomAvatarUrl(),
      role: index === 0 
        ? 'Team Lead' 
        : pickRandomFromArray(['Support Agent', 'Technical Support', 'Billing Specialist']),
      department: pickRandomFromArray(['Customer Support', 'Technical', 'Billing', 'Sales']),
      status: pickRandomFromArray(['active', 'away', 'offline']),
      joinedAt: getRandomPastDate(3).toISOString()
    };
  });
};

// Utility functions to replace faker
function generateRandomID() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

function generateRandomNumericString(length) {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10);
  }
  return result;
}

function getRandomFirstName() {
  const firstNames = ['John', 'Jane', 'Michael', 'Emily', 'David', 'Sarah', 'Robert', 'Lisa', 'William', 'Emma', 
    'Thomas', 'Olivia', 'James', 'Sophia', 'Richard', 'Ava', 'Joseph', 'Isabella', 'Charles', 'Mia'];
  return pickRandomFromArray(firstNames);
}

function getRandomLastName() {
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Jones', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore', 'Taylor', 
    'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Garcia', 'Martinez', 'Robinson'];
  return pickRandomFromArray(lastNames);
}

function getRandomCompanyName() {
  const companies = ['Acme Inc', 'Globex Corp', 'Soylent Corp', 'Initech', 'Umbrella Corp', 'Stark Industries', 
    'Wayne Enterprises', 'Cyberdyne Systems', 'Massive Dynamic', 'Oscorp Industries'];
  return pickRandomFromArray(companies);
}

function getRandomAvatarUrl() {
  const id = Math.floor(Math.random() * 1000);
  return `https://i.pravatar.cc/150?img=${id}`;
}

function getRandomPhoneNumber() {
  return `+1 ${generateRandomNumericString(3)}-${generateRandomNumericString(3)}-${generateRandomNumericString(4)}`;
}

function getRandomPastDate(years) {
  // Create a date in May 2025
  const may2025 = new Date(2025, 4, 15); // Month is 0-based, so 4 = May
  
  // Calculate a random number of days in the past (within the month of May)
  const daysAgo = Math.floor(Math.random() * 30); // Up to 30 days ago
  
  // For longer time periods (if years > 0.5), allow dates from previous months
  const totalDaysAgo = years > 0.5 ? Math.floor(years * 365 * Math.random()) : daysAgo;
  
  // Create the past date by subtracting days
  const pastDate = new Date(may2025);
  pastDate.setDate(may2025.getDate() - totalDaysAgo);
  
  // Ensure we don't go earlier than May 1, 2025 if we want to stay in May
  if (years <= 0.5 && pastDate.getMonth() !== 4) { // 4 = May
    pastDate.setDate(1); // Set to first day of May
    pastDate.setMonth(4);
  }
  
  return pastDate;
}

function getRandomDateBetween(start, end) {
  // Ensure the end date is May 31, 2025 at the latest
  const latestDate = new Date(2025, 4, 31); // Month is 0-based, so 4 = May
  const adjustedEnd = end > latestDate ? latestDate : end;
  
  // Generate a random time between start date and adjusted end date
  const startTime = start.getTime();
  const endTime = adjustedEnd.getTime();
  const randomTime = startTime + Math.random() * (endTime - startTime);
  
  return new Date(randomTime);
}

function getRandomSentence(min, max) {
  const phrases = [
    'I need help with my account',
    'Could you assist me with a refund',
    'The website is not working properly',
    'I have a question about my subscription',
    'How do I update my profile information',
    'There seems to be an error with my payment',
    'Can you provide more details about your services',
    'I would like to suggest a new feature',
    'The mobile app is crashing on startup',
    'I am experiencing technical difficulties',
    'My login credentials are not working',
    'I need to change my payment method',
    'When will my order be delivered',
    'The checkout process is not completing',
    'How do I cancel my subscription'
  ];
  
  const length = Math.floor(Math.random() * (max - min + 1)) + min;
  const sentence = [];
  
  for (let i = 0; i < length; i++) {
    sentence.push(pickRandomFromArray(phrases));
  }
  
  return sentence.join('. ') + '.';
}

function getRandomParagraph(min, max) {
  const length = Math.floor(Math.random() * (max - min + 1)) + min;
  const sentences = [];
  
  for (let i = 0; i < length; i++) {
    sentences.push(getRandomSentence(1, 1));
  }
  
  return sentences.join(' ');
}

function pickRandomFromArray(array) {
  return array[Math.floor(Math.random() * array.length)];
}
